import { Phone, Mail, MapPin, Clock, Navigation } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { LOGO_URL } from "@/lib/api";

const MAP_ADDRESS = "Gardenes iela 11, Zemgales priekšpilsēta, Rīga, LV-1002";
const MAP_QUERY = encodeURIComponent(MAP_ADDRESS);

export const ContactSection = ({ content }) => (
  <section id="kontakti" className="relative border-t border-white/10 bg-[#0a0a0e] py-24 sm:py-32">
    <div className="mx-auto max-w-[1500px] px-5 sm:px-10">
      <SectionLabel num="/ 06">Kontakti</SectionLabel>
      <Reveal>
        <h2 className="mt-5 font-head text-4xl font-extrabold uppercase italic leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
          <span className="metal-text">SAZINIETIES</span> <span className="crimson-text">AR MUMS</span>
        </h2>
      </Reveal>

      <div className="mt-12 grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
        <Reveal className="bg-[#0c0c11] p-8">
          <a
            href={`tel:${(content.phone || "").replace(/\s/g, "")}`}
            data-testid="contact-phone"
            className="group block"
          >
            <Phone className="h-7 w-7 text-rose-500" strokeWidth={1.5} />
            <div className="mt-5 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">Zvani mums</div>
            <div className="mt-2 font-head text-3xl font-extrabold text-white transition-colors group-hover:text-rose-400 sm:text-4xl">
              {content.phone}
            </div>
          </a>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <Mail className="h-4 w-4 text-rose-500" /> {content.email}
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <MapPin className="h-4 w-4 text-rose-500" /> {content.address}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="bg-[#0c0c11] p-8">
          <Clock className="h-7 w-7 text-rose-500" strokeWidth={1.5} />
          <div className="mt-5 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">Darba laiks</div>
          <div className="mt-4 divide-y divide-white/10" data-testid="contact-hours">
            {[content.hours_weekday, content.hours_saturday, content.hours_sunday].map((h, i) => (
              <div key={i} className="py-3 font-head text-lg uppercase tracking-wide text-slate-200">
                {h}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.16} className="relative flex flex-col justify-between overflow-hidden bg-[#0c0c11] p-8">
          <div className="hatch absolute inset-0 opacity-25" />
          <img src={LOGO_URL} alt="TURBOLUX AUTOSERVISS" className="relative h-12 w-auto object-contain" />
          <p className="relative mt-6 text-sm leading-relaxed text-slate-400">
            {content.announcement}
          </p>
          <a
            href="#pieteikums"
            data-testid="contact-cta"
            className="relative mt-8 inline-flex items-center justify-center bg-rose-600 px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-rose-500"
          >
            Pieteikt vizīti
          </a>
        </Reveal>
      </div>

      {/* map */}
      <Reveal delay={0.1} className="mt-6">
        <div className="relative border border-white/10 bg-[#0c0c11]" data-testid="contact-map">
          <div className="flex flex-wrap items-center gap-3 border-b border-white/10 px-5 py-4 sm:px-8">
            <MapPin className="h-5 w-5 text-rose-500" strokeWidth={1.6} />
            <div className="min-w-0">
              <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">Mūsu darbnīca</div>
              <div className="mt-1 font-head text-base font-bold uppercase tracking-tight text-white md:text-lg">
                {MAP_ADDRESS}
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`}
                target="_blank"
                rel="noreferrer"
                data-testid="map-open-link"
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-white/40 hover:text-white"
              >
                Atvērt Google Maps
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`}
                target="_blank"
                rel="noreferrer"
                data-testid="map-directions-link"
                className="inline-flex items-center gap-2 border border-rose-600/60 bg-rose-600/10 px-4 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] text-rose-300 transition-colors hover:bg-rose-600 hover:text-white"
              >
                Norādes <Navigation className="h-3 w-3" />
              </a>
            </div>
          </div>
          <div className="relative h-[320px] w-full overflow-hidden sm:h-[420px]">
            <iframe
              title="TURBOLUX AUTOSERVISS karte"
              data-testid="contact-map-iframe"
              src={`https://www.google.com/maps?q=${MAP_QUERY}&z=16&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 h-full w-full border-0"
              style={{ filter: "grayscale(1) invert(0.9) contrast(0.92) brightness(0.95)" }}
            />
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_40px_rgba(0,0,0,0.75)]" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
